import { PaginationToolbarContext } from "@/context/PaginationToolbarContext";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained.tsx";
import { ChevronLeft, LayoutGrid, PlusIcon, Table2 } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  DatatablePagination,
  DatatablePaginationProps,
} from "../ui/datatable-pagination";
import {
  persistBoPreference,
  readBoPreferenceRaw,
} from "@/lib/user-preferences/store";

export interface RelatedLinksListGridLayoutProps {
  title: ReactNode;
  isAddMode: boolean;
  onBack: () => void;
  onToggleAdd: () => void;
  /** Defaults to `linkedEntries` (e.g. use `relatedRecords` for complex artwork links). */
  backLabel?: ReactNode;
  searchInput?: ReactNode;
  addModeTable?: ReactNode;
  listTable: ReactNode;
  gridContent: ReactNode;
  relatedPagination?: DatatablePaginationProps;
  storageKey?: string;
}

export function RelatedLinksListGridLayout({
  title,
  isAddMode,
  onBack,
  onToggleAdd,
  backLabel,
  searchInput,
  addModeTable,
  listTable,
  gridContent,
  relatedPagination,
  storageKey,
}: RelatedLinksListGridLayoutProps) {
  const { t } = useTranslation();
  const resolvedBackLabel = backLabel ?? t("linkedEntries");
  const viewStorageKey = useMemo(
    () => (storageKey ? `${storageKey}-view` : null),
    [storageKey],
  );
  const [view, setView] = useState<"list" | "grid">(() => {
    if (!viewStorageKey) return "list";
    try {
      const saved =
        readBoPreferenceRaw(viewStorageKey) ??
        localStorage.getItem(viewStorageKey);
      return saved === "grid" ? "grid" : "list";
    } catch {
      return "list";
    }
  });

  useEffect(() => {
    if (isAddMode || !viewStorageKey) return;
    try {
      persistBoPreference(viewStorageKey, view);
    } catch {
      // ignore preference persistence failures
    }
  }, [isAddMode, view, viewStorageKey]);

  const viewToggle = (
    <TabsList className="grid w-fit grid-cols-2 h-9">
      <TabsTrigger value="grid" className="px-2">
        <LayoutGrid className="h-4 w-4" />
      </TabsTrigger>
      <TabsTrigger value="list" className="px-2">
        <Table2 className="h-4 w-4" />
      </TabsTrigger>
    </TabsList>
  );

  return (
    <div className="w-full">
      <Tabs
        value={view}
        onValueChange={(value) => setView(value as "list" | "grid")}
      >
        {isAddMode && (
          <div
            className="flex items-center gap-1 mb-4 cursor-pointer"
            onClick={onBack}
          >
            <ChevronLeft className="w-3 h-3 text-gray-300" />
            <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
              {resolvedBackLabel}
            </p>
          </div>
        )}
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          {!isAddMode && (
            <Button variant="outline" onClick={onToggleAdd}>
              <PlusIcon className="w-4 h-4 mr-2" />
              {t("add")}
            </Button>
          )}
          {isAddMode && searchInput}
        </div>

        {isAddMode && addModeTable}

        <TabsContent value="list">
          {!isAddMode && (
            <PaginationToolbarContext.Provider value={viewToggle}>
              {listTable}
            </PaginationToolbarContext.Provider>
          )}
        </TabsContent>
        <TabsContent value="grid">
          {!isAddMode && (
            <>
              {relatedPagination && (
                <>
                  <DatatablePagination
                    {...relatedPagination}
                    endContent={viewToggle}
                  />
                  <div className="w-full flex gap-8 flex-wrap">{gridContent}</div>
                  <div className="w-full my-2">
                    <DatatablePagination {...relatedPagination} />
                  </div>
                </>
              )}
              {!relatedPagination && (
                <div className="w-full flex gap-8 flex-wrap">{gridContent}</div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
